import { useEffect, useState } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';

import Button from '@klv/atoms/button';
import Icon from '@klv/atoms/icon';
import { Wrapper } from '@klv/utils/wrapper';

import { generateClassName, windowIsDefined } from '@klv/shared/utils/functions';
import { getBackgroundType } from '@klv/utils/wrapper/functions';

import * as styles from './styles/navigationCategorySelection.module.scss';
import { sortCategories } from './utils/functions';
import type { ICategory, INavigationCategorySelection } from './types';

const NavigationCategorySelection = ({
  activeCategory = '',
  backgroundColor = 'white',
  categories = [],
  customPadding = { customPaddingBottom: 'md', customPaddingTop: 'md' },
  displayAllCategoriesByDefault = false,
  fadeIn,
  headline = '',
  top8CategoryList,
}: INavigationCategorySelection) => {
  const { t } = useTranslation();
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [isMobile, setIsMobile] = useState(windowIsDefined() && window.innerWidth <= 1024);
  const [buttonText, setButtonText] = useState('View all categories');
  const [filteredCategories] = useState(sortCategories(categories, top8CategoryList));  
  const  [currentCategories, setCurrentCategories] = useState<ICategory[]>((displayAllCategoriesByDefault || showAllCategories || isMobile) ? filteredCategories : filteredCategories.slice(0, 8));

  const backgroundType = getBackgroundType(backgroundColor);

  useEffect(() => {
    const handleResize = () => {
      if (windowIsDefined())
        setIsMobile(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (displayAllCategoriesByDefault || showAllCategories || isMobile) {
      setCurrentCategories(filteredCategories); 
    } else {
      // filter for top 8 categories
      setCurrentCategories(filteredCategories.slice(0, 8));
    }

    if (showAllCategories) {
      setButtonText(t('organisms_navigationCategorySelection_viewLessButton_text'));
    } else {
      setButtonText(t('organisms_navigationCategorySelection_viewAllButton_text'));
    }
  }, [displayAllCategoriesByDefault, filteredCategories, isMobile, showAllCategories, buttonText]);

  return (
    <Wrapper
      className={generateClassName([styles.wrapperStyle])}
      contained={false}
      grid={true}
      customPadding={customPadding}
      inlinePadding={isMobile ? false : true}
      fadeIn={fadeIn ? .25 : 0}
      backgroundColor={backgroundColor}
    >

      <div className={styles.contentContainer}>
        {headline && <h5 className={generateClassName(['h5', styles.headline])}>{headline}</h5>}
      </div>

      <div className={generateClassName([styles.categories])}>
        {currentCategories.map(({
          id, name, link,
        }) => (
          <Button
            aria-current={activeCategory === name}
            buttonColor={backgroundType === 'light' ? 'light' : 'dark'}
            key={id}
            text={name}
            url={link}
            variant="pill"
          />
        ),
        )}
      </div>
      {!displayAllCategoriesByDefault && (
        <div className={generateClassName([styles.contentContainer, styles.displayCategoryButton])}>
            <button
              title={buttonText}
              onClick={()=> setShowAllCategories(!showAllCategories)}
              className={styles.customTertiary}
            >
              <span dangerouslySetInnerHTML={{ __html: buttonText }}/>
                <Icon size={.7} iconName="ChevronDown" fillColor="currentColor" className={generateClassName([styles.styledIcon, showAllCategories ? styles.isExpanded : ''])} />
            </button>
        </div>
      )}
  </Wrapper>
  );
};

export default NavigationCategorySelection;


